import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { api } from "@/lib/api-client"

import { getWorkflowRunFromApiError } from "../utils/workflow-run-chat-utils"

export type FlowRunStatus = "queued" | "running" | "paused" | "succeeded" | "failed"

// A single input field surfaced on a user-input pause, so the resume card can
// render one labelled control per field. Optional/back-compat: legacy and
// non-multi-field user-input pauses omit `fields` and use a single textarea.
export type PendingInterruptField = {
  key: string
  type: string
  label?: string
  required?: boolean
}

export type PendingInterrupt = {
  type?: string
  node_id?: string
  input_id?: string
  prompt?: string
  preview?: string
  // Present only on multi-field user-input pauses. When set, the resume card
  // renders one control per field and submits `field_responses: {key: value}`.
  fields?: PendingInterruptField[]
}

export type FlowRunDiagnosticEvent = {
  at: string
  type: string
  message: string
  details?: Partial<Record<string, string | number | boolean | null | string[]>>
}

export type FlowRunDiagnostics = {
  schema: "flow_run_diagnostics/v1"
  events: FlowRunDiagnosticEvent[]
  model_resolutions?: Record<
    string,
    {
      requested_model?: string | null
      effective_model?: string | null
      requested_reasoning_effort?: string | null
      reasoning_effort?: string | null
      api_surface?: string
      legacy_model_default?: boolean
      configuration_fallback?: boolean
    }
  >
}

export type FlowRun = {
  id: string
  workflow_id: string
  owner: string
  trigger: "manual" | "schedule" | "webhook" | "document_indexed"
  status: FlowRunStatus
  workflow_title?: string | null
  inputs: Record<string, unknown>
  output: string
  error?: string | null
  started_at: string
  finished_at?: string | null
  // HITL (Lever 2): set when status === "paused" — what the run is waiting on.
  pending_interrupt?: PendingInterrupt | null
  // Set by the backend on a paused run: false when the run can't actually be
  // resumed (e.g. the interrupt has expired), so the UI shouldn't offer it.
  resumable?: boolean
  // Bounded, content-free model/tool events captured by headless Flow runs.
  diagnostics?: FlowRunDiagnostics
}

const runsKey = (workflowId: string) => ["workflow-runs", workflowId] as const

export function useWorkflowRuns(workflowId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: runsKey(workflowId ?? ""),
    enabled: Boolean(workflowId) && enabled,
    // Poll while the panel is open so a queued/running/paused run updates live
    // (e.g. a run reaches "paused" awaiting approval, or finishes after resume).
    refetchInterval: (query) => {
      const data = query.state.data as FlowRun[] | undefined
      const active = (data ?? []).some(
        (r) => r.status === "queued" || r.status === "running" || r.status === "paused"
      )
      return enabled && active ? 4000 : false
    },
    queryFn: async (): Promise<FlowRun[]> => {
      const res = (await api.get(`/explorer/workflow/${workflowId}/runs`)) as unknown as {
        runs?: FlowRun[]
      }
      return res.runs ?? []
    },
  })
}

export function useRunWorkflow(workflowId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { question?: string; inputs?: Record<string, unknown> } = {}) => {
      return (await api.post(`/explorer/workflow/${workflowId}/run`, body)) as unknown as FlowRun
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: runsKey(workflowId) })
    },
  })
}

/**
 * Resume a paused run (HITL). For an approval node, pass `{ decision: "approve" }`
 * or `{ decision: "deny", reason }`; the decision is delivered to the paused
 * interrupt() and the run continues.
 */
export function useResumeWorkflowRun(workflowId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  return useMutation({
    mutationFn: async ({
      runId,
      decision,
    }: {
      runId: string
      decision: Record<string, unknown>
    }) => {
      return (await api.post(
        `/explorer/workflow/${workflowId}/runs/${runId}/resume`,
        decision
      )) as unknown as FlowRun
    },
    onError: (error, variables) => {
      const failedRun = getWorkflowRunFromApiError(error)
      if (
        failedRun?.status === "failed" &&
        failedRun.id === variables.runId &&
        failedRun.workflow_id === workflowId
      ) {
        queryClient.setQueryData<FlowRun[]>(runsKey(workflowId), (runs) =>
          (runs ?? []).map((run) => (run.id === failedRun.id ? failedRun : run))
        )
        toast.error(t("toast.runFailed", "Flow run failed"), {
          description: failedRun.error ?? undefined,
        })
        return
      }
      toast.error(t("runs.actionFailed", "Could not submit your decision"))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: runsKey(workflowId) })
    },
  })
}
